
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import { syncGameFunnelSteps } from '@/composables/template-editor/TemplateEditor';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

interface SlotSymbol {
  id: string;
  type: string;
  image: string;
  text?: string;
  label: string;
}

interface WinCombination {
  symbols: string[];
  prize: string;
  probability: number;
  stock?: number | null;
}

export default defineComponent({
  name: 'GameSlotCombinationsEditor',

  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const combinations = ref<WinCombination[]>([]);
    const availableSymbols = ref<SlotSymbol[]>([]);

    const totalProbability = computed(() => {
      return combinations.value.reduce((sum, combo) => sum + (combo.probability || 0), 0);
    });

    const parseSymbolsFromElement = () => {
      try {
        // Read data-symbols from the actual DOM element in the iframe
        const iframeDoc = getTemplateIframeDocument();
        if (iframeDoc) {
          const slotEl = iframeDoc.querySelector('.spm_widget_game_slot_machine');
          if (slotEl) {
            const raw = slotEl.getAttribute('data-symbols');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed) && parsed.length > 0) {
                availableSymbols.value = parsed.map((s: any) => ({
                  id: s.id || '',
                  type: s.type || 'emoji',
                  image: s.image || '',
                  text: s.text || '',
                  label: s.label || s.image || s.id || '',
                }));
                return;
              }
            }
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }

      // Default symbols
      availableSymbols.value = [
        { id: 'cherry', type: 'emoji', image: '🍒', label: 'Cerise' },
        { id: 'lemon', type: 'emoji', image: '🍋', label: 'Citron' },
        { id: 'orange', type: 'emoji', image: '🍊', label: 'Orange' },
        { id: 'seven', type: 'emoji', image: '7️⃣', label: 'Sept' },
      ];
    };

    const getSymbolDisplay = (symbolId: string): string => {
      const symbol = availableSymbols.value.find(s => s.id === symbolId);
      if (!symbol) return '❓';
      if (symbol.type === 'image') return '';
      return symbol.text || symbol.image || '❓';
    };

    const isImageSymbol = (symbolId: string): boolean => {
      const symbol = availableSymbols.value.find(s => s.id === symbolId);
      return symbol?.type === 'image' || (!!(symbol?.image) && /^(https?:\/\/|\/)/.test(symbol?.image || ''));
    };

    const getSymbolImage = (symbolId: string): string => {
      const symbol = availableSymbols.value.find(s => s.id === symbolId);
      return symbol?.image || '';
    };

    /**
     * Stock limits are edited in the separate "Stock" accordion (GameStockEditor), which writes
     * them straight into the iframe attribute. This editor keeps its own copy of the combinations
     * and rewrites the whole attribute on every change, so the stock of every combination is
     * re-read from the iframe (source of truth) before each write (SC-3828).
     */
    const syncStockFromIframe = () => {
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;
        const element = iframeDoc.querySelector(props.parserValues.selector);
        if (!element) return;
        const iframeCombinations = JSON.parse(element.getAttribute('data-win-combinations') || '[]');
        if (!Array.isArray(iframeCombinations)) return;
        combinations.value.forEach((_combo: WinCombination, index: number) => {
          const stock = Number(iframeCombinations[index]?.stock);
          combinations.value[index].stock = stock > 0 ? stock : null;
        });
      } catch (e) {
        // Unreadable attribute: keep the local copy
      }
    };

    const parseInitialValue = () => {
      parseSymbolsFromElement();

      try {
        const value = property.value.value;
        if (value && typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            combinations.value = parsed.map((item: any) => ({
              symbols: item.symbols || ['cherry', 'cherry', 'cherry'],
              prize: item.prize || '',
              probability: item.probability || 0,
              stock: (item.stock != null && Number(item.stock) > 0) ? Number(item.stock) : null,
            }));
            syncStockFromIframe();
            return;
          }
        }
      } catch (e) {
        console.warn('[GameSlotCombinationsEditor] Failed to parse initial value:', e);
      }

      // Default combinations if parsing failed
      combinations.value = [
        { symbols: ['seven', 'seven', 'seven'], prize: '50% de réduction', probability: 2 },
        { symbols: ['cherry', 'cherry', 'cherry'], prize: '20% de réduction', probability: 10 },
      ];
    };

    const emitChange = () => {
      const jsonValue = JSON.stringify(combinations.value);
      property.value.value = jsonValue;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
      // The field setter only applies this value ~50 ms later: write it right away (same JSON) so the
      // iframe is already up to date when the next change re-reads the stock by position (SC-3828)
      try {
        const element = getTemplateIframeDocument()?.querySelector(props.parserValues.selector);
        if (element) {
          element.setAttribute('data-win-combinations', jsonValue);
        }
      } catch (e) {
        // Iframe not available: the field setter will apply the value
      }
    };

    const onCombinationChange = () => {
      syncStockFromIframe();
      emitChange();
      syncGameFunnelSteps();
    };

    const addCombination = () => {
      syncStockFromIframe();
      const defaultSymbol = availableSymbols.value[0]?.id || 'cherry';

      combinations.value.push({
        symbols: [defaultSymbol, defaultSymbol, defaultSymbol],
        prize: 'Nouveau prix',
        probability: 5,
      });
      emitChange();
      syncGameFunnelSteps();
    };

    const removeCombination = (index: number) => {
      if (combinations.value.length > 1) {
        // Read the stocks before the splice so each stock follows its combination, not its position
        syncStockFromIframe();
        combinations.value.splice(index, 1);
        emitChange();
        syncGameFunnelSteps();
      }
    };

    // Watch for data-symbols changes in iframe via MutationObserver
    let symbolsObserver: MutationObserver | null = null;

    const startObservingSymbols = () => {
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;
        const slotEl = iframeDoc.querySelector('.spm_widget_game_slot_machine');
        if (!slotEl) return;

        symbolsObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-symbols') {
              parseSymbolsFromElement();
              break;
            }
          }
        });
        symbolsObserver.observe(slotEl, { attributes: true, attributeFilter: ['data-symbols'] });
      } catch (e) {
        // Ignore
      }
    };

    onMounted(() => {
      parseInitialValue();
      nextTick(() => {
        syncGameFunnelSteps();
        startObservingSymbols();
      });
    });

    onBeforeUnmount(() => {
      if (symbolsObserver) {
        symbolsObserver.disconnect();
        symbolsObserver = null;
      }
    });

    // Re-read symbols when switching back to this tab
    watch(() => props.parserValues, () => {
      parseSymbolsFromElement();
      // Re-attach observer in case iframe was rebuilt
      if (symbolsObserver) symbolsObserver.disconnect();
      nextTick(() => startObservingSymbols());
    }, { immediate: false });

    watch(() => props.parserValues.properties[0]?.value, () => {
      parseInitialValue();
      nextTick(() => syncGameFunnelSteps());
    }, { deep: true });

    return {
      t,
      combinations,
      availableSymbols,
      totalProbability,
      getSymbolDisplay,
      isImageSymbol,
      getSymbolImage,
      onCombinationChange,
      addCombination,
      removeCombination,
    };
  },
});
